import React, { useState, useMemo } from 'react';
import { allCountryCodes } from './countryCodes.js';

const UserFormTable = ({ onFormSubmit, isLoading, design, customColor, customFont, customTextColor }) => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    countryCode: '+1', // Default country code
    name: ''
  });
  const [isMobile, setIsMobile] = useState(false);
  const [filteredCountryCodes, setFilteredCountryCodes] = useState([]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };


  const countryCodes = allCountryCodes

  // New: Country code input attributes to help with autofill
  const countryCodeAttributes = {
    autoComplete: 'tel-country-code',
    'data-country-code': true,
    inputMode: 'numeric',
    pattern: '\\+[0-9]{1,4}',
  };
  

  const handleCountryCodeChange = (e) => {
    setFormData({ ...formData, countryCode: e.target.value });
  };
  const handleCountryCodeInputChange = (e) => {
    const value = e.target.value;
    let processedValue = value;

    // If input doesn't start with +, add it
    if (value && !value.startsWith('+') && /^\d/.test(value)) {
      processedValue = '+' + value;
    }

    setFormData({ ...formData, countryCode: processedValue });

    // Filter the country codes based on input
    if (value) {
      const filtered = countryCodes.filter((country) =>
        country.name.toLowerCase().includes(value.toLowerCase()) ||
        country.code.includes(processedValue)
      );
      setFilteredCountryCodes(filtered);

      // If exact match found for country name, use its code
      const exactMatch = countryCodes.find(country => 
        country.name.toLowerCase() === value.toLowerCase()
      );
      if (exactMatch) {
        setFormData(prev => ({ ...prev, countryCode: exactMatch.code }));
      }
    } else {
      setFilteredCountryCodes([]);
    }
  };

  // New: Handle country code field blur
  const handleCountryCodeBlur = (e) => {
    const value = e.target.value;
    if (value) {
      // Try to find a matching country
      const matchedCountry = countryCodes.find(country => 
        country.name.toLowerCase() === value.toLowerCase() ||
        country.code === value
      );
      
      if (matchedCountry) {
        setFormData(prev => ({ ...prev, countryCode: matchedCountry.code }));
      }
    }
    // Clear filtered results with slight delay
    setTimeout(() => setFilteredCountryCodes([]), 200);
  };
  const handleCountryCodeSelect = (code) => {
    setFormData({ ...formData, countryCode: code });
    setFilteredCountryCodes([]); // Clear the suggestions after selection
  };
  

  const [errors, setErrors] = useState({});

  const validatePhoneNumber = (phoneNumber) => {
    const digitPattern = /^\d+$/; // Pattern to check if all characters are digits
    const validLengthPattern = /^\d{10,15}$/; // Pattern to check if the number is between 10 and 15 digits

    if (phoneNumber.length < 10) {
      return { isValid: false, message: 'Phone number is too short. It must be at least 10 digits.' };
    }
    
    if (!digitPattern.test(phoneNumber)) {
      return { isValid: false, message: 'Phone number should only contain digits' };
    }

    

    if (!validLengthPattern.test(phoneNumber)) {
      return { isValid: false, message: 'Phone number must be between 10 and 15 digits.' };
    }

    return { isValid: true, message: '' }; // Return valid if both conditions pass
  };


  const validateForm = () => {
    const newErrors = {};

    // Check if name is empty
    if (!formData.name.trim()) {
      newErrors.name = 'Name cannot be empty';
    }

    // Check if phone number is valid
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      newErrors.phoneNumber = phoneValidation.message;
    }
    const validCountryCode = countryCodes.some(
      (country) => country.code === formData.countryCode
    );
    if (!validCountryCode) {
      newErrors.countryCode = 'Invalid country code';
    }

    setErrors(newErrors);

    // If there are no errors, the form is valid
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent form from refreshing the page
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }
    setErrors({});
    onFormSubmit(formData);

  };

  const buttonStyle = useMemo(() => {
    // If customColor is provided, use it
    if (customColor && customColor.trim() !== '') {
      return {
        backgroundColor: customColor,
        color: '#FFFFFF',
        hoverBg: customColor,
        padding: '0.75rem 1.5rem',
        fontSize: '1.125rem',
        height: 'auto',
        minHeight: '3rem',
        borderRadius: '9999px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem'
      };
    }

    switch (design) {
      case 'kate':
        return {
          backgroundColor: '#4ABA75',
          color: '#FFFFFF',
          hoverBg: '#3D9D62',
          padding: '0.75rem 1.5rem',
          fontSize: '1.125rem',
          height: 'auto',
          minHeight: '3rem',
          borderRadius: '9999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        };
      case 'orange':
        return {
          backgroundColor: '#eb9637',
          color: '#FFFFFF',
          hoverBg: '#d88730',
          padding: '0.5rem 1rem',
          fontSize: '1rem',
          height: 'auto',
          minHeight: '2.5rem',
          borderRadius: '0.375rem'
        };
      case 'cloobeck':
        return {
          backgroundColor: '#1430AD',
          color: '#FFFFFF',
          hoverBg: '#1430AD',
          padding: '0.5rem 1rem',
          fontSize: '1rem',
          height: 'auto',
          minHeight: '2.5rem',
          borderRadius: '0.375rem'
        };
      case 'lgreen':
        return {
          backgroundColor: '#32CD32',
          color: '#FFFFFF',
          hoverBg: '#32CD32',
          padding: '0.5rem 1rem',
          fontSize: '1rem',
          height: 'auto',
          minHeight: '2.5rem',
          borderRadius: '0.375rem'
        };
      case 'yobidark':
        return {
          background: 'linear-gradient(225deg, #0ea5e9 0%, #2dd4bf 100%)',
          backgroundColor: '#0ea5e9', // Fallback color (sky-500)
          color: '#FFFFFF',
          hoverBg: '#0284c7', // sky-600
          padding: '0.5rem 1rem',
          fontSize: '1rem',
          height: 'auto',
          minHeight: '2.5rem',
          borderRadius: '0.375rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        };
      default:
        return {
          backgroundColor: '#4B0082', // Changed to purple-900
          color: '#FFFFFF',
          hoverBg: '#3B0062', // Slightly lighter purple for hover
          padding: '0.5rem 1rem',
          fontSize: '1rem',
          height: 'auto',
          minHeight: '2.5rem',
          borderRadius: '0.375rem'
        };
    }
  }, [design, customColor]);

  const buttonText = design === 'kate' ? 'Talk to Kate' : 'Talk Now';


  const LoadingSpinner = useMemo(() => () => (
    <svg className={`animate-spin h-5 w-5 ${design === 'yobidark' ? 'text-blue-400' : 'text-purple-500'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  ), [design]);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center w-full"
      style={{
        ...(customFont && { fontFamily: customFont }),
        ...(customTextColor && { color: customTextColor })
      }}
    >
      <div className="mb-4 w-full max-w-lg">
        <div className="flex w-full">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter your name"
            className={`py-2 px-4 border rounded-lg w-full ${design === 'yobidark' ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-400' : ''}`}
          />
        </div>
        {errors.name && <p className={`text-sm mt-1 ${design === 'yobidark' ? 'text-red-400' : 'text-red-500'}`}>{errors.name}</p>}
      </div>

      <div className="mb-4 w-full max-w-lg">
        <div className="flex w-full relative">
          <input
            type="text"
            name="countryCode"
            value={formData.countryCode}
            onChange={handleCountryCodeInputChange}
            onBlur={handleCountryCodeBlur}
            placeholder="Enter country code or country"
            className={`py-2 px-4 border rounded-l-lg w-1/4 ${design === 'yobidark' ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-400' : ''}`}
            {...countryCodeAttributes}
          />

          {filteredCountryCodes.length > 0 && (
            <ul className="absolute top-full left-0 w-1/4 bg-white border mt-1 z-10 max-h-40 overflow-y-auto">
              {filteredCountryCodes.map((country) => (
                <li
                  key={country.code}
                  className="cursor-pointer py-2 px-4 hover:bg-gray-100"
                  onClick={() => handleCountryCodeSelect(country.code)}
                >
                  {country.name} ({country.code})
                </li>
              ))}
            </ul>
          )}

          <input
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="Enter your phone"
            className={`py-2 px-4 border rounded-r-lg w-3/4 ${design === 'yobidark' ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-400' : ''}`}
            autoComplete="tel-national"
          />
        </div>
        {errors.countryCode && <p className={`text-sm mt-1 ${design === 'yobidark' ? 'text-red-400' : 'text-red-500'}`}>{errors.countryCode}</p>}
        {errors.phoneNumber && <p className={`text-sm mt-1 ${design === 'yobidark' ? 'text-red-400' : 'text-red-500'}`}>{errors.phoneNumber}</p>}
      </div>

      <button
        className={`w-full max-w-lg mx-auto
                ${isMobile ? `active:bg-${buttonStyle.hoverBg}` : `hover:bg-${buttonStyle.hoverBg}`}
                flex items-center justify-center transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm sm:text-base`}
        style={{
          backgroundColor: buttonStyle.backgroundColor,
          color: buttonStyle.color,
          padding: buttonStyle.padding,
          fontSize: buttonStyle.fontSize,
          height: buttonStyle.height,
          minHeight: buttonStyle.minHeight,
          borderRadius: buttonStyle.borderRadius,
          display: buttonStyle.display,
          alignItems: buttonStyle.alignItems,
          justifyContent: 'center',
          gap: buttonStyle.gap
        }}
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span className="ml-2">Connecting...</span>
          </>
        ) : (
          <span>{buttonText}</span>
        )}
      </button>
    </form>
  );
};

export default UserFormTable;